import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';

/**
 * Section component for a website's hero section
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  // External field mappings
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  /* -------------------- Helpers inline (same style) -------------------- */

  const StarIcon = ({ size = '1.2rem' }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill="#FFD700"
      color="#FFD700"
      viewBox="0 0 22 22"
      aria-hidden="true"
    >
      <path d="M12.8649 2.99628C12.4796 2.33213 11.5204 2.33213 11.1351 2.99628L8.42101 7.67359C8.23064 8.00168 7.90159 8.22557 7.52653 8.28222L2.44021 9.05044C1.58593 9.17947 1.28627 10.2581 1.95158 10.8093L5.74067 13.9485C6.09141 14.2391 6.25633 14.6975 6.17113 15.1449L5.17996 20.35C5.02327 21.1729 5.88706 21.8122 6.62821 21.4219L11.4176 18.9001C11.7821 18.7082 12.2178 18.7082 12.5824 18.9001L17.3718 21.4219C18.1129 21.8122 18.9767 21.1729 18.82 20.35L17.8289 15.1449C17.7437 14.6975 17.9086 14.2391 18.2593 13.9485L22.0484 10.8093C22.7137 10.2581 22.4141 9.17947 21.5598 9.05044L16.4735 8.28222C16.0984 8.22557 15.7694 8.00168 15.579 7.67359L12.8649 2.99628Z" />
    </svg>
  );

  // 2️⃣ Sub-headline (debajo del título)
  const SubHeadline = () => (
    <p
      style={{
        fontSize: '1rem',
        color: 'white',
        marginTop: '0.5rem',
        textAlign: 'center',
        maxWidth: '32ch',
        marginInline: 'auto',
        fontWeight: 600,
        lineHeight: 1.35,
      }}
    >
      Reserva en pocos minutos, con pagos 100&nbsp;% seguros y cobertura veterinaria 24/7.
    </p>
  );

  // 4️⃣ Trust strip (con separadores de barra)
  const TrustStrip = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column', // Stack children vertically
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.55)',
          padding: '0.6rem 1rem',
          borderRadius: '0.9rem',
          marginTop: '1rem',
          color: 'white',
          fontSize: '0.85rem',
          fontWeight: 500,
          lineHeight: 1.2,
          textAlign: 'center',
          gap: '0.7rem', // Space between lines
          minHeight: '1.2em',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.15rem', whiteSpace: 'nowrap' }}>
          <StarIcon size="1rem" />
          <strong style={{ fontWeight: 600 }}>4.9</strong>&nbsp;/&nbsp;250+&nbsp;reseñas
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>500+ noches reservadas</span>
        <span style={{ whiteSpace: 'nowrap' }}>Reseñas reales de dueños</span>
      </div>
    );
  };

  // Testimonial (item)
  const Testimonial = ({ quote, author, location }) => (
    <figure
      style={{
        color: 'white',
        textAlign: 'center',
        fontSize: '0.95rem',
        lineHeight: 1.35,
        margin: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.2rem',
          marginBottom: '0.35rem',
        }}
      >
        {[...Array(5)].map((_, i) => (
          <StarIcon key={i} size="1rem" />
        ))}
      </div>
      <blockquote style={{ margin: 0, fontStyle: 'italic' }}>&ldquo;{quote}&rdquo;</blockquote>
      <figcaption style={{ marginTop: '0.25rem', opacity: 0.85 }}>
        — {author}, {location}
      </figcaption>
    </figure>
  );

  // Testimonials (estático)
  const Testimonials = () => (
    <div
      style={{
        marginTop: '1.25rem',
        display: 'grid',
        gap: '0.9rem',
        fontWeight: 600,
      }}
      aria-label="Testimonios"
    >
      <Testimonial
        quote="Los cuidadores fueron tan cariñosos que casi no me echa de menos."
        author="Luis"
        location="Barcelona"
      />
    </div>
  );

  /* --------------------------- Render principal --------------------------- */

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields && (
        <header className={defaultClasses.sectionDetails}>
          <Field
            data={title}
            className={defaultClasses.title}
            options={fieldOptions}
          />
          <SubHeadline />
          <Field
            data={description}
            className={defaultClasses.description}
            options={fieldOptions}
          />
          <Field
            data={callToAction}
            className={defaultClasses.ctaButton}
            options={fieldOptions}
          />
          <TrustStrip />
          <Testimonials />
        </header>
      )}
    </SectionContainer>
  );
};

export default SectionHero;
